/**
 * App.tsx — INFINI Pioneer Registration
 *
 * Responsibilities:
 *   • Routes all 5 screens via currentScreen in pioneerStore
 *   • Fires PostHog page() + track() on every screen transition
 *   • Instantiates useRemainingSeats once at the root → passes to screens that need it
 *   • Owns Supabase anon session bootstrap (run once on mount)
 *   • Stripe return-URL handler (if user lands back after redirect)
 *
 * Import map (adjust paths to match your project structure):
 *   screens/   → Screen1Landing, Screen2BirthData, Screen3ChartReading,
 *                Screen4PioneerOffer, Screen5Confirmation
 *   hooks/     → useAnalytics, useRemainingSeats, usePayment
 *   store/     → usePioneerStore
 *   lib/       → supabase (createClient instance)
 */

import { useEffect, useRef } from 'react'
import { usePioneerStore }    from './store/pioneerStore'
import { useAnalytics }       from './hooks/useAnalytics'
import { useRemainingSeats }  from './hooks/useRemainingSeats'
import { usePayment }         from './hooks/usePayment'
import { supabase }           from './lib/supabase'

// ── Screen components ──────────────────────────────────────────────────────
import Screen1Landing      from './screens/Screen1Landing'
import Screen2BirthData    from './screens/Screen2BirthData'
import Screen3ChartReading from './screens/Screen3ChartReading'
import Screen4PioneerOffer from './screens/Screen4PioneerOffer'
import Screen5Confirmation from './screens/Screen5Confirmation'

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────

const SCREEN_NAMES: Record<number, string> = {
  1: 'landing',
  2: 'birth_data',
  3: 'chart_reading',
  4: 'pioneer_offer',
  5: 'confirmation',
}

// ─────────────────────────────────────────────
// Root component
// ─────────────────────────────────────────────

export default function App() {
  // ── Store ──────────────────────────────────
  const currentScreen    = usePioneerStore((s) => s.currentScreen)
  const setUserId        = usePioneerStore((s) => s.setUserId)
  const setScreen        = usePioneerStore((s) => s.setScreen)
  const setPioneerSeat   = usePioneerStore((s) => s.setPioneerSeat)
  const setPaymentStatus = usePioneerStore((s) => s.setPaymentStatus)
  const setPaymentError  = usePioneerStore((s) => s.setPaymentError)

  // ── Analytics (PostHog) ────────────────────
  const { track, page } = useAnalytics()

  /**
   * STUB REPLACEMENT #3a
   * Was: SeatCounter imported remainingSeats from a hardcoded constant.
   * Now: real-time data from Supabase via useRemainingSeats hook.
   *
   * Lifted to App root so Screen1 and Screen4 share one subscription.
   */
  const { remainingSeats, totalSeats, isLoading: seatsLoading } = useRemainingSeats()

  /**
   * STUB REPLACEMENT #2 (hook instantiation)
   * Was: initiateStripeCheckout() in Screen4PioneerOffer was a stub.
   * The usePayment hook is instantiated here (or in Screen4 — see note below).
   *
   * NOTE: If Screen4 renders conditionally you can instantiate usePayment
   * directly inside Screen4PioneerOffer instead, since hooks must not be
   * called conditionally. The callbacks below show what Screen4 needs to
   * invoke on button click.
   *
   * The hook contract expected from usePayment:
   *   const { initiateCheckout, isLoading, error } = usePayment({
   *     onSuccess: (sessionId) => { ... },
   *     onError:   (message)   => { ... },
   *   })
   */
  const { initiateCheckout, isLoading: paymentLoading } = usePayment({
    onSuccess: (sessionId: string) => {
      setPaymentStatus('processing')
      // Stripe will redirect; on return the webhook confirms the seat.
      // stripeSessionId stored for polling/confirmation fallback.
      usePioneerStore.getState().setStripeSessionId(sessionId)
    },
    onError: (message: string) => {
      setPaymentStatus('failed')
      setPaymentError(message)
      track('payment_error', { message })
    },
  })

  // ── Analytics: fire on every screen change ─
  const prevScreenRef = useRef<number | null>(null)

  useEffect(() => {
    const screenName = SCREEN_NAMES[currentScreen]
    page(`pioneer_${screenName}`)

    if (prevScreenRef.current !== null && prevScreenRef.current !== currentScreen) {
      track('screen_transition', {
        from:      SCREEN_NAMES[prevScreenRef.current] ?? 'unknown',
        to:        screenName,
        direction: currentScreen > prevScreenRef.current ? 'forward' : 'back',
        remaining_seats: remainingSeats,
      })
    }

    prevScreenRef.current = currentScreen
  }, [currentScreen])   // eslint-disable-line react-hooks/exhaustive-deps
  // ↑ intentionally omitting track/page/remainingSeats to fire once per screen change

  // ── Screen-specific analytics ──────────────
  useEffect(() => {
    if (currentScreen === 1) track('landing_viewed', { remaining_seats: remainingSeats })
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Supabase: bootstrap anonymous session ──
  useEffect(() => {
    const bootstrapSession = async () => {
      const { data: { session } } = await supabase.auth.getSession()

      if (session?.user) {
        setUserId(session.user.id)
        return
      }

      // Create anonymous user so Supabase RLS policies can scope their data
      const { data, error } = await supabase.auth.signInAnonymously()
      if (error) {
        console.error('[INFINI] Supabase anon sign-in failed:', error.message)
        return
      }
      if (data.user) {
        setUserId(data.user.id)
        track('session_created', { user_id: data.user.id })
      }
    }

    bootstrapSession()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Stripe return-URL handler ──────────────
  // Handles the case where Stripe redirects back to /?payment=success|cancel
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const paymentResult = params.get('payment')

    if (paymentResult === 'success') {
      // Webhook has already fired assign-pioneer-seat;
      // poll Supabase for the seat and advance to Screen 5.
      setPaymentStatus('success')
      track('payment_stripe_return', { result: 'success' })
      pollForPioneerSeat()
    } else if (paymentResult === 'cancel') {
      setPaymentStatus('idle')
      setScreen(4)
      track('payment_stripe_return', { result: 'cancel' })
    }

    // Clean up query params from URL
    if (paymentResult) {
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────
  // Helper: poll for pioneer seat after Stripe return
  // ─────────────────────────────────────────────
  const pollForPioneerSeat = async () => {
    const { userId } = usePioneerStore.getState()
    if (!userId) return

    let attempts = 0
    const MAX_ATTEMPTS = 10
    const INTERVAL_MS  = 2000

    const poll = setInterval(async () => {
      attempts++

      const { data, error } = await supabase
        .from('pioneer_seats')
        .select('serial_number, seat_number, tier, assigned_at, user_id')
        .eq('user_id', userId)
        .eq('payment_status', 'paid')
        .maybeSingle()

      if (error) {
        console.error('[INFINI] Seat poll error:', error.message)
      }

      if (data) {
        clearInterval(poll)
        setPioneerSeat({
          serialNumber: data.serial_number,
          seatNumber:   data.seat_number,
          tier:         data.tier,
          assignedAt:   data.assigned_at,
          userId:       data.user_id,
        })
        setScreen(5)
        track('pioneer_seat_confirmed', {
          serial_number: data.serial_number,
          tier:          data.tier,
          seat_number:   data.seat_number,
        })
      }

      if (attempts >= MAX_ATTEMPTS) {
        clearInterval(poll)
        // Seat not found after polling — show fallback state in Screen 5
        console.warn('[INFINI] Seat not confirmed after polling. Manual check needed.')
        setScreen(5)
      }
    }, INTERVAL_MS)
  }

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#09081A] text-white font-['DM_Sans'] antialiased">

      {/* ── Screen 1: Landing ──────────────────────────────────────────── */}
      {currentScreen === 1 && (
        <Screen1Landing
          remainingSeats={remainingSeats}
          totalSeats={totalSeats}
          seatsLoading={seatsLoading}
          onCTAClick={() => {
            track('cta_clicked', { screen: 'landing', remaining_seats: remainingSeats })
            setScreen(2)
          }}
        />
      )}

      {/* ── Screen 2: Birth Data ───────────────────────────────────────── */}
      {currentScreen === 2 && (
        /**
         * STUB REPLACEMENT #1 (usage site)
         * Screen2BirthData previously had:
         *   const handleSubmit = () => { /* calculateChart stub }
         *
         * Now: call store.calculateChart() — it POSTs to /api/calculate-chart
         * and auto-advances to Screen 3 on success.
         *
         * Screen2BirthData must:
         *   1. Call store.setBirthData(formValues) before calculateChart()
         *   2. Call store.calculateChart() on form submit
         *   3. Read store.chartLoading + store.chartError for UI state
         */
        <Screen2BirthData
          onAnalyticsEvent={(event, props) => track(event, props)}
        />
      )}

      {/* ── Screen 3: Chart Reading ────────────────────────────────────── */}
      {currentScreen === 3 && (
        <Screen3ChartReading
          onContinue={() => {
            track('chart_reading_continued')
            setScreen(4)
          }}
          onBack={() => {
            track('chart_reading_back')
            setScreen(2)
          }}
        />
      )}

      {/* ── Screen 4: Pioneer Offer ────────────────────────────────────── */}
      {currentScreen === 4 && (
        /**
         * STUB REPLACEMENT #2 (usage site)
         * Screen4PioneerOffer previously had:
         *   const initiateStripeCheckout = () => { /* TODO }
         *
         * Now: receives initiateCheckout from usePayment hook (instantiated
         * above at App root). Screen4 calls this prop on pay button click.
         *
         * Screen4PioneerOffer must:
         *   1. Call store.setSelectedTier(tier) when user picks a tier
         *   2. Call initiateCheckout({ tier, userId, userEmail? }) on pay click
         *   3. Read store.paymentStatus + store.paymentError for button state
         */
        <Screen4PioneerOffer
          remainingSeats={remainingSeats}
          seatsLoading={seatsLoading}
          initiateCheckout={initiateCheckout}
          paymentLoading={paymentLoading}
          onTierSelected={(tier) => {
            track('tier_selected', { tier, remaining_seats: remainingSeats })
          }}
          onBack={() => {
            track('pioneer_offer_back')
            setScreen(3)
          }}
        />
      )}

      {/* ── Screen 5: Confirmation ─────────────────────────────────────── */}
      {currentScreen === 5 && (
        <Screen5Confirmation
          onShareClick={(platform) => track('seat_shared', { platform })}
        />
      )}

    </div>
  )
}
